import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { setStoredToken } from './lib/api';
import { brand } from './lib/brand';
import { neutralizeStorefrontShellHash } from './lib/storefrontHash';
import { detectInitialLanguage, ensureLanguage, setI18nLanguage } from './lib/i18n';
import { isTenantSubdomainHost } from './lib/tenantHost';
import { installLabelFocusGuard } from './lib/labelFocusGuard';

// Apex cabinet hands the existing login to the photographer host, then drops it from the address.
function takeCabinetHandoffToken() {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw.includes('cabinet=')) return;
  const token = new URLSearchParams(raw).get('cabinet');
  if (!token) return;
  setStoredToken(token);
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
}

takeCabinetHandoffToken();
installLabelFocusGuard();

// Before React paints module anchors — avoids refresh scroll-to-#main-content.
neutralizeStorefrontShellHash();

// Brand chrome only on apex (e.g. sitonaim.com). Tenant hosts already get landing/SEO
// title from SPA HTML inject — do not overwrite with platform displayName.
if (brand.id !== 'catalogofworks' && brand.displayName && !isTenantSubdomainHost()) {
  document.title = brand.displayName;
  const siteName = document.head.querySelector('meta[property="og:site_name"]');
  if (siteName) {
    siteName.setAttribute('content', brand.displayName);
  }
}

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/pwa/sw.js?v=9');
  });
}

const initialLang = detectInitialLanguage();
setI18nLanguage(initialLang);

// Paint only once the dictionary is here — otherwise the first frame would be textless.
void ensureLanguage(initialLang).then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
